import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  noIndex?: boolean;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonicalPath,
  noIndex = false,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image'
}) => {
  const location = useLocation();
  const baseUrl = 'https://ctcwaterworks.com'; // Force HTTPS base URL
  
  // Use provided canonical path or current path
  const canonicalUrl = `${baseUrl}${canonicalPath || location.pathname}`;
  
  // Default title and description if not provided
  const defaultTitle = 'CTC Waterworks - Agricultural Water Solutions';
  const defaultDescription = 'Leading provider of agricultural water solutions including sulfur burners, hydrocoolers, DPA drenchers, and irrigation filtration systems.';
  
  const pageTitle = title ? `${title} | CTC Waterworks` : defaultTitle;
  const pageDescription = description || defaultDescription;
  
  // Default OG image if not provided
  const defaultOgImage = `${baseUrl}/CTC_Waterworks Logo.png`;
  const ogImageUrl = ogImage || defaultOgImage;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      
      {/* Canonical Tag */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Indexing Control */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph Tags */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImageUrl} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );
};

export default SEOHead;